import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    FormControl,
    Select,
    MenuItem,
    Link,
    Paper,
    Stack,
    Tooltip,
    Switch,
    FormControlLabel,
    keyframes
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { getCurrentData, getGEXHeatmap } from '../api/client';
import MetricsRow from './MetricsRow';
import GEXHeatmap from './GEXHeatmap';
import SecondaryCharts from './SecondaryCharts';
import MomentumCluster from './MomentumCluster';
import ExpirationAnalysis from './ExpirationAnalysis';
import HistoryView from './HistoryView';
import FinancialDisclaimer from './FinancialDisclaimer';
import { useAuth } from '../context/AuthContext';
import { Button } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import WavesIcon from '@mui/icons-material/Waves';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const AVAILABLE_TICKERS = ['SPY', 'QQQ', 'IWM', 'IBIT', 'ETHA'];

const pulseEffect = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
);

const Dashboard = () => {
    const { logout } = useAuth();
    const [selectedTicker, setSelectedTicker] = useState('SPY');
    const [mainTab, setMainTab] = useState(0); // 0: Dashboard, 1: History
    const [loading, setLoading] = useState(true);
    const [transitioning, setTransitioning] = useState(true); // For smooth ticker switch
    const [error, setError] = useState(null);
    const [currentData, setCurrentData] = useState(null);
    const [gexData, setGEXData] = useState(null);
    const [showWhales, setShowWhales] = useState(false);
    const [selectedExpirations, setSelectedExpirations] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchData = useCallback(async () => {
        const startTime = Date.now();
        try {
            // Only show full loading spinner on initial load
            if (!currentData) setLoading(true);
            setError(null);

            const current = await getCurrentData(selectedTicker);
            setCurrentData(current);

            // Auto-select first 6 expirations if none selected
            const expirations = selectedExpirations.length > 0
                ? selectedExpirations
                : current.expirations.slice(0, 6);

            setSelectedExpirations(expirations);

            const gex = await getGEXHeatmap(selectedTicker, expirations);
            setGEXData(gex);

            setLastUpdated(new Date());
            setLoading(false);

            // Enforce minimum loading time of 750ms to ensure the spinner is visible
            const elapsed = Date.now() - startTime;
            if (elapsed < 750) {
                await new Promise(resolve => setTimeout(resolve, 750 - elapsed));
            }
            setTransitioning(false); // End transition
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to fetch data from API');
            setLoading(false);
            setTransitioning(false);
        }
    }, [selectedTicker, selectedExpirations, currentData]);

    // Refetch data when ticker changes
    const handleTickerChange = (event, newTicker) => {
        if (newTicker !== null && newTicker !== selectedTicker) {
            setTransitioning(true); // Start fade-out
            setSelectedTicker(newTicker);
            setSelectedExpirations([]); // Reset expirations for new ticker
            // Keep old data visible during transition (don't null it out)
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedTicker]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const interval = setInterval(() => {
            fetchData();
        }, 60000); // 1 minute
        return () => clearInterval(interval);
    }, [fetchData]);

    if (loading && !currentData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            {/* Financial Disclaimer Banner */}
            <FinancialDisclaimer />

            {/* Header with User Info and Logout */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 2 }}>
                <Button
                    variant="text"
                    size="small"
                    startIcon={<LogoutIcon />}
                    onClick={logout}
                    sx={{
                        color: '#ff4d4d',
                        textTransform: 'none',
                        '&:hover': { background: 'rgba(255, 77, 77, 0.1)' }
                    }}
                >
                    Logout
                </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 4, position: 'relative' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3, // Increased gap
                    mb: 1
                }}>
                    <Box
                        component="img"
                        src="/assets/logo.png"
                        alt="GammaFlow"
                        sx={{
                            width: { xs: 40, md: 60 },
                            height: { xs: 40, md: 60 },
                            filter: 'drop-shadow(0 0 10px rgba(0, 212, 170, 0.3))'
                        }}
                    />
                    <Typography variant="h1" sx={{
                        background: 'linear-gradient(90deg, #00d4aa 0%, #7b68ee 50%, #ff6b6b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        fontWeight: 900,
                        letterSpacing: '-0.02em',
                    }}>
                        GammaFlow
                    </Typography>

                    {/* TICKER SELECTOR (DROPDOWN STYLE) */}
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                            value={selectedTicker}
                            onChange={(e) => handleTickerChange(null, e.target.value)}
                            sx={{
                                color: '#00d4aa',
                                fontWeight: 900,
                                fontSize: '1rem',
                                letterSpacing: '0.1em',
                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(0, 212, 170, 0.3)',
                                borderRadius: '8px',
                                '& .MuiSelect-select': {
                                    py: 0.5,
                                    px: 2,
                                },
                                '& fieldset': { border: 'none' },
                                '&:hover': {
                                    bgcolor: 'rgba(0, 212, 170, 0.1)',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: 'rgba(0, 212, 170, 0.7)',
                                },
                                boxShadow: '0 0 15px rgba(0, 212, 170, 0.1)',
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: '#0f172a',
                                        border: '1px solid rgba(0, 212, 170, 0.3)',
                                        mt: 1,
                                        '& .MuiMenuItem-root': {
                                            color: '#94a3b8',
                                            fontWeight: 700,
                                            '&:hover': {
                                                bgcolor: 'rgba(0, 212, 170, 0.1)',
                                                color: '#00d4aa',
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: 'rgba(0, 212, 170, 0.2)',
                                                color: '#00d4aa',
                                                '&:hover': {
                                                    bgcolor: 'rgba(0, 212, 170, 0.3)',
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {AVAILABLE_TICKERS.map(ticker => (
                                <MenuItem key={ticker} value={ticker}>
                                    {ticker}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Typography variant="body2" color="text.secondary">
                    Tiered Market Intelligence • Real-time Dealer Positioning
                </Typography>
                {lastUpdated && (
                    <Typography variant="caption" color="text.secondary">
                        Last updated: {lastUpdated.toLocaleTimeString()}
                    </Typography>
                )}
            </Box>

            {/* Main Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)} centered>
                    <Tab label="Dashboard" />
                    <Tab label="Historical" />
                </Tabs>
            </Box>

            {/* View 1: Live Dashboard */}
            <TabPanel value={mainTab} index={0}>
                <Box sx={{ position: 'relative', minHeight: 400 }}>
                    {/* Loading Overlay */}
                    {transitioning && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            pt: '30vh',
                            gap: 2,
                            background: '#0a0e17',
                            zIndex: 99,
                        }}>
                            <CircularProgress size={50} thickness={5} sx={{ color: '#00d4aa' }} />
                            <Typography variant="h6" sx={{ color: '#00d4aa', fontWeight: 600, animation: `${pulseEffect} 1.5s infinite ease-in-out` }}>
                                Updating Data...
                            </Typography>
                        </Box>
                    )}
                    {currentData && gexData && (
                        <MetricsRow spyPrice={currentData.spy_price} keyLevels={gexData.key_levels} ticker={selectedTicker} />
                    )}

                    {gexData && (
                        <Box sx={{ maxWidth: '1400px', mx: 'auto', mt: 4 }}>
                            <Grid container spacing={3} sx={{ flexWrap: { lg: 'nowrap', xs: 'wrap' }, alignItems: 'stretch' }}>
                                {/* Heatmap Column (Flexible) */}
                                <Grid item xs={12} lg sx={{ minWidth: 0, display: 'flex', flexGrow: 1 }}>
                                    <Box sx={{ width: '100%' }}>
                                        <GEXHeatmap
                                            heatmapData={gexData.heatmap}
                                            spyPrice={currentData.spy_price}
                                            gammaFlip={gexData.key_levels.gamma_flip}
                                            flowAlerts={gexData.flow_alerts}
                                            showWhales={showWhales}
                                        />
                                    </Box>
                                </Grid>

                                {/* Analysis Column (Fixed 350px) */}
                                <Grid item xs={12} lg="auto" sx={{ display: 'flex' }}>
                                    <Box sx={{
                                        width: { lg: '350px', xs: '100%' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: 0,
                                        minHeight: '100%'
                                    }}>
                                        <Box sx={{ mb: 2 }}>
                                            <MomentumCluster data={currentData.momentum} />
                                        </Box>

                                        {/* OPTION FLOW SECTION */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WavesIcon sx={{ fontSize: '1rem', color: '#3b82f6' }} />
                                                Option Flow Analysis
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={showWhales}
                                                        onChange={(e) => setShowWhales(e.target.checked)}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: '#3b82f6',
                                                                '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.04)' },
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: '#3b82f6',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>Whales</Typography>}
                                                labelPlacement="start"
                                                sx={{ m: 0, '& .MuiFormControlLabel-label': { mr: 0.5 } }}
                                            />
                                        </Box>

                                        {/* INSTITUTIONAL BIAS METER */}
                                        {gexData.bias_meter && (
                                            <Paper sx={{
                                                p: 2,
                                                mb: 2,
                                                background: 'rgba(30, 41, 59, 0.7)',
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(255, 255, 255, 0.08)'
                                            }}>
                                                <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    Institutional Bias
                                                    <Tooltip title="Net Bullish/Bearish Volume ratio relative to Total Unusual Volume. Measures the directional conviction of large institutional trades." arrow placement="top">
                                                        <InfoOutlinedIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', cursor: 'help', '&:hover': { color: '#fff' } }} />
                                                    </Tooltip>
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Box sx={{ flexGrow: 1, height: 8, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: '50%',
                                                            height: '100%',
                                                            width: `${Math.abs(gexData.bias_meter.bias_score) * 50}%`,
                                                            bgcolor: gexData.bias_meter.bias_score >= 0 ? '#00d4aa' : '#ff6b6b',
                                                            transform: gexData.bias_meter.bias_score >= 0 ? 'translateX(0)' : 'translateX(-100%)',
                                                            transition: 'width 1s ease-out'
                                                        }} />
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#ff6b6b', fontWeight: 700 }}>PUTS</Typography>
                                                    <Typography sx={{ fontSize: '0.65rem', color: gexData.bias_meter.bias_score >= 0 ? '#00d4aa' : '#ff6b6b', fontWeight: 900 }}>
                                                        {gexData.bias_meter.bias_score >= 0 ? 'BULLISH' : 'BEARISH'} ({(Math.abs(gexData.bias_meter.bias_score) * 100).toFixed(0)}%)
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#00d4aa', fontWeight: 700 }}>CALLS</Typography>
                                                </Box>
                                            </Paper>
                                        )}

                                        {/* LIVE FLOW INSIGHTS */}
                                        {gexData.flow_alerts && gexData.flow_alerts.length > 0 && (
                                            <Paper sx={{
                                                p: 2,
                                                mb: 2,
                                                background: 'rgba(30, 41, 59, 0.7)',
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                                maxHeight: 320,
                                                overflowY: 'auto',
                                                /* God Tier Hover-Only Scrollbar */
                                                '&::-webkit-scrollbar': {
                                                    width: '5px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: 'transparent',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: 'rgba(255, 255, 255, 0.08)',
                                                    borderRadius: '10px',
                                                },
                                                '&:hover::-webkit-scrollbar-thumb': {
                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                },
                                                '&::-webkit-scrollbar-thumb:hover': {
                                                    background: 'rgba(255, 255, 255, 0.35)',
                                                }
                                            }}>
                                                <Typography variant="overline" sx={{ color: '#3b82f6', fontWeight: 900, mb: 1.5, display: 'block' }}>
                                                    🐋 Live Flow Insights
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {[...gexData.flow_alerts]
                                                        .sort((a, b) => {
                                                            // Primary Sort: Detection Time (Chronological - Newest First)
                                                            if (a.detected_at && b.detected_at) {
                                                                return b.detected_at.localeCompare(a.detected_at);
                                                            }
                                                            // Fallback: Expiry then Significance
                                                            const dateDiff = a.expiry.localeCompare(b.expiry);
                                                            if (dateDiff !== 0) return dateDiff;
                                                            return (b.significance_score || 0) - (a.significance_score || 0);
                                                        })
                                                        .filter(alert => (alert.significance_score || 0) >= 5.0)
                                                        .slice(0, 25)
                                                        .map((alert, i) => {
                                                            const color = alert.type === 'Call' ? '#00d4aa' : '#ff6b6b';
                                                            const significance = alert.significance_score || 0;
                                                            const isKraken = significance >= 50;
                                                            const isMega = significance >= 20 && significance < 50;

                                                            // Harden freshness check against clock drift
                                                            let isFresh = false;
                                                            if (alert.detected_at) {
                                                                const diff = new Date() - new Date(alert.detected_at);
                                                                const minutesElapsed = diff / 60000;
                                                                isFresh = minutesElapsed > -0.5 && minutesElapsed < 5;
                                                            }

                                                            return (
                                                                <Box key={i} sx={{
                                                                    p: 1.2,
                                                                    bgcolor: isKraken ? `${color}11` : 'rgba(255,255,255,0.02)',
                                                                    borderRadius: 1.5,
                                                                    borderLeft: `3px solid ${color}`,
                                                                    border: isKraken ? `1px solid ${color}44` : 'none',
                                                                    boxShadow: isKraken ? `0 0 15px ${color}11` : 'none',
                                                                    position: 'relative',
                                                                    transition: 'transform 0.2s',
                                                                    '&:hover': { transform: 'translateX(2px)', bgcolor: 'rgba(255,255,255,0.05)' }
                                                                }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: '#fff' }}>
                                                                                ${alert.strike} {alert.type}s
                                                                            </Typography>
                                                                            {isKraken && (
                                                                                <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, px: 0.5, py: 0.1, bgcolor: color, color: '#000', borderRadius: 0.5 }}>
                                                                                    KRAKEN
                                                                                </Typography>
                                                                            )}
                                                                            {isMega && (
                                                                                <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, px: 0.5, py: 0.1, border: `1px solid ${color}`, color: color, borderRadius: 0.5 }}>
                                                                                    MEGA
                                                                                </Typography>
                                                                            )}
                                                                            {isFresh && (
                                                                                <Box sx={{
                                                                                    px: 0.6,
                                                                                    py: 0.1,
                                                                                    borderRadius: 0.5,
                                                                                    bgcolor: 'rgba(255, 183, 77, 0.1)',
                                                                                    border: '1px solid rgba(255, 183, 77, 0.4)',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center'
                                                                                }}>
                                                                                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, color: '#ffb74d', letterSpacing: '0.05em' }}>
                                                                                        NEW
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                                                                            {alert.expiry}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                                                        Vol: {alert.volume.toLocaleString()} (<b>{alert.confidence.toFixed(1)}x</b> OI)
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                </Stack>
                                            </Paper>
                                        )}

                                        {/* HEATMAP INSIGHTS */}
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
                                            <AnalyticsIcon sx={{ fontSize: '1rem', color: '#00d4aa' }} />
                                            Heatmap Insights
                                        </Typography>

                                        <Paper sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'hidden',
                                            p: 2,
                                            background: 'rgba(30, 41, 59, 0.7)',
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 255, 255, 0.08)',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <ExpirationAnalysis analysisData={gexData.analysis} />
                                        </Paper>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {gexData && (
                        <Box sx={{ mt: 6, maxWidth: '1400px', mx: 'auto' }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, mb: 2, display: 'block' }}>
                                Secondary GEX Analysis
                            </Typography>
                            <SecondaryCharts
                                aggregated={gexData.aggregated}
                                heatmapData={gexData.heatmap}
                                spyPrice={currentData.spy_price}
                            />
                        </Box>
                    )}
                </Box>
            </TabPanel>

            {/* View 2: History Lab */}
            <TabPanel value={mainTab} index={1}>
                <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, mb: 2, display: 'block' }}>
                        Historical GEX Evolution
                    </Typography>
                    <HistoryView ticker={selectedTicker} />
                </Box>
            </TabPanel>

            {/* Legal Footer for Compliance */}
            <Box sx={{ mt: 8, mb: 4, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)', pt: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                    By using GammaFlow, you agree to our{' '}
                    <Link component={RouterLink} to="/terms" sx={{ color: '#00d4aa', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link component={RouterLink} to="/privacy" sx={{ color: '#00d4aa', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Privacy Policy
                    </Link>.
                </Typography>
            </Box>

        </Container >
    );
};

export default Dashboard;
